import { useQuery } from "@tanstack/react-query";
import { Button } from "antd";
import { useMemo, useState } from "react";
import ReportEditButton from "../../../components/Button/ReportEditButton";
import ReportingExportButton from "../../../components/Button/ReportingExportButton";
import AuditLogModal from "../../../components/Modal/AuditLogModal";
import OfiPDFTemplate from "../../../components/PDF/Reporting/ofi/OfiPDFTemplate";
import QueryRetry from "../../../components/QueryRetry";
import ReportItemsPanel from "../../../components/Reporting/items/ReportItemsPanel";
import ReportCreatedLine from "../../../components/Reporting/ReportCreatedLine";
import {
	getAssetCard,
	getProjectFacts,
	getReviewerFact,
	getTenantFact,
} from "../../../components/Reporting/reportFacts";
import ReportHeader, {
	ReportHeaderFact,
} from "../../../components/Reporting/ReportHeader";
import { useGlobalLoading } from "../../../hooks/useGlobalLoading";
import { useLookup } from "../../../hooks/useLookup";
import useReportDocumentsDrawer from "../../../hooks/useReportDocumentsDrawer";
import useReportEditor from "../../../hooks/useReportEditor";
import useReportItemNames from "../../../hooks/useReportItemNames";
import { getOFIList } from "../../../services/api-server/ofi";
import { GetAntIcon } from "../../../utils/ant_icons";
import { createOFILookupConfig } from "../../../utils/reporting/lookupConfigs/ofi";
import { getReportAuthorEmail } from "../../../utils/reporting/reportAuthor";
import { REPORT_ITEM_KINDS } from "../../../utils/reporting/reportItemKinds";
import { getReportStatusDisplay } from "../../../utils/reporting/statusDisplay";
import { handleExportReport } from "../../../utils/reports";

type OfiOverviewProps = {
	reportId: string;
};

const ACTIONS = REPORT_ITEM_KINDS.follow_up_actions;

const OfiOverview = ({ reportId }: Readonly<OfiOverviewProps>) => {
	const [auditLogOpen, setAuditLogOpen] = useState(false);

	const { data, isLoading, isError } = useQuery({
		queryKey: ["report-overview", reportId],
		queryFn: async () => {
			const response = await getOFIList({ _id: reportId });
			if (response.length === 0) throw new Error("Report not found");
			return response[0];
		},
	});

	const editor = useReportEditor("ofi", data, ["report-overview", reportId]);
	const documents = useReportDocumentsDrawer("ofi", reportId, data);

	useGlobalLoading("ofi-overview", isLoading);

	const isRejected = data?.is_rejected === true;
	const statusDisplay = getReportStatusDisplay(
		data?.status,
		data?.open_status,
		isRejected
	);

	const lookupConfig = useMemo(
		() => createOFILookupConfig([data?.operator]),
		[data?.operator]
	);

	const { dataWithLookup, isLoading: isLoadingLookup } = useLookup(
		["lookup", "report-overview", reportId],
		data ? [data] : [],
		lookupConfig,
		0
	);
	const lookupData = dataWithLookup[0];

	// From the looked-up report, so the export keeps the names its lookup
	// found.
	const { emailToName, reportWithNames, isLoadingNames } = useReportItemNames(
		["report-actions-responsible", reportId],
		lookupData,
		ACTIONS
	);

	if (isError) {
		// The drawers stay, so a failed background refetch doesn't close them.
		return (
			<>
				<QueryRetry queryKey={["report-overview", reportId]} />
				{documents.drawer}
				{editor.drawer}
			</>
		);
	}

	// An OFI has no actions before its initial review: its reviewer writes them
	// then, and a draft's drawer has no actions tab. So a draft is opened from
	// the header, and the actions panel says who adds them.
	const isDraft = data?.status === "draft";
	const isBeforeReview =
		isDraft || (data?.status === "open" && data?.open_status === "in_review");
	// Any stored value that says so, as the stream and the review history read
	// it: a report filed anonymously names nobody.
	const isAnonymous = Boolean(data?.is_anonymous);

	const facts: ReportHeaderFact[] = [
		...getProjectFacts(lookupData, isLoadingLookup),
		getReviewerFact(lookupData, isLoadingLookup),
		getTenantFact(data?.operator),
	];

	const created = (
		<ReportCreatedLine
			submittedOn={data?.submitted_on}
			authorName={lookupData?.author_name}
			authorEmail={getReportAuthorEmail(data)}
			isAnonymous={isAnonymous}
			historyTitle="OFI review history"
			historyData={lookupData ?? data}
			onOpenAuditLog={() => setAuditLogOpen(true)}
		/>
	);

	return (
		<>
			{data && (
				// Keyed by report, like the actions panel: another OFI opened in
				// place starts expanded, with its description clamped.
				<ReportHeader
					key={reportId}
					label="OFI details"
					reportNumber={data.report_number}
					title={data.title}
					status={statusDisplay}
					actions={
						<>
							{isDraft && editor.canEdit && (
								<ReportEditButton
									type="default"
									label="Edit"
									onClick={editor.open}
								/>
							)}
							<Button icon={GetAntIcon("file2")} onClick={documents.open}>
								Additional documents
							</Button>
							<ReportingExportButton
								type="default"
								onExport={() =>
									handleExportReport(
										// The PDF's approval record names the author, so an
										// anonymous OFI's says "Anonymous", as the page does.
										isAnonymous
											? { ...reportWithNames, author_name: "Anonymous" }
											: reportWithNames,
										"ofi",
										OfiPDFTemplate
									)
								}
								isLoading={isLoadingLookup || isLoadingNames}
							/>
						</>
					}
					created={created}
					facts={facts}
					media={getAssetCard(lookupData, isLoadingLookup)}
					summaries={[
						{
							key: "description",
							label: "Description",
							text: data.description,
						},
					]}
				>
					{/* Keyed by report, so another OFI opened in place starts with
					    its own filters and open rows. */}
					<ReportItemsPanel
						key={reportId}
						kind={ACTIONS}
						items={reportWithNames?.follow_up_actions ?? []}
						emailToName={emailToName}
						report={data}
						canEditItem={editor.canEditItem}
						onEditItem={editor.openItem}
						onAddItems={editor.canEdit && !isDraft ? editor.open : undefined}
						emptyText={isBeforeReview ? "No follow-up actions yet" : undefined}
						emptyHint={
							isBeforeReview
								? "The reviewer adds them during review."
								: undefined
						}
					/>
				</ReportHeader>
			)}
			{/* Outside the header, so collapsing it never closes the log. */}
			{data && (
				<AuditLogModal
					auditlogFilter={{ level: "ofi", level_id: data._id }}
					label="Opportunity for Improvements report"
					type="reporting"
					open={auditLogOpen}
					handleClose={() => {
						setAuditLogOpen(false);
					}}
				/>
			)}
			{documents.drawer}
			{editor.drawer}
		</>
	);
};

export default OfiOverview;
